import { uniq } from 'es-toolkit';

import { Batch, BatchWithoutId } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import {
  getProductionComponentInstancesByBatchGuid,
  getProductionComponentInstancesForActiveProductionGroupNotBatchedByAnalysisSetGuid,
} from '../selectors/batches';
import { getLoadedTrussesByAnalysisSetGuid } from '../selectors/trusses';
import { batchCreated, batchDeleted, batchesLoaded } from '../store/batchesSlice';
import { productionComponentInstancesLoaded } from '../store/productionComponentInstancesSlice';
import { removeProductionComponentInstancesFromBatch } from './productionComponentInstances';
import { AsyncThunkActionVoid } from './thunkActions';

export function loadBatchesForProductionGroup(productionGroupGuid: string, spinner = true): AsyncThunkActionVoid {
  return async function loadBatchesForProjectThunkAction(dispatch, getState) {
    const spinnerId = spinner ? dispatch(addSpinnerMessage('Loading batches for production group...')) : null;

    try {
      const state = getState();

      if (Object.values(state.batches).some((batch) => batch.productionGroupGuid === productionGroupGuid)) return;

      const batchesForProject = await designService.batches.getForProductionGroup(productionGroupGuid);
      dispatch(batchesLoaded(batchesForProject));
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error loading batches'));
    } finally {
      if (spinnerId != null) dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function createBatchFromSelected(): AsyncThunkActionVoid {
  return async function createBatchFromSelectedThunkAction(dispatch, getState) {
    const spinnerId = dispatch(addSpinnerMessage('Creating batch...'));

    try {
      const state = getState();
      const { activeProductionGroupGuid: productionGroupGuid, checkedAnalysisSetGuids } = state.batchesAndBundlesTab;

      if (!productionGroupGuid) throw new Error('Attempted to create batch with no production group active.');

      const batchWithoutId: BatchWithoutId = {
        guid: crypto.randomUUID(),
        productionGroupGuid,
      };
      const batchWithId = await designService.batches.insert(batchWithoutId);
      dispatch(batchCreated(batchWithId));

      const productionComponentInstancesNotBatchedByAnalysisSetGuid =
        getProductionComponentInstancesForActiveProductionGroupNotBatchedByAnalysisSetGuid(state);
      const productionComponentInstanceGuidsToUpdate = checkedAnalysisSetGuids
        .flatMap((analysisSetGuid) => productionComponentInstancesNotBatchedByAnalysisSetGuid[analysisSetGuid])
        .map((productionComponentInstance) => productionComponentInstance.guid);
      const updatedProductionComponentInstances = await designService.productionComponentInstances.patchAddToBatch(
        productionComponentInstanceGuidsToUpdate,
        batchWithId.guid,
      );
      dispatch(productionComponentInstancesLoaded(updatedProductionComponentInstances));
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error creating batch'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function addSelectedToBatch(): AsyncThunkActionVoid {
  return async function addSelectedToBatchThunkAction(dispatch, getState) {
    const spinnerId = dispatch(addSpinnerMessage('Adding to batch...'));

    try {
      const state = getState();

      const { batchBeingEditedGuid, checkedAnalysisSetGuids } = state.batchesAndBundlesTab;
      if (batchBeingEditedGuid === null) {
        dispatch(enqueueError('No batch selected to add components to.'));
        return;
      }

      const productionComponentInstancesByBatchGuid = getProductionComponentInstancesByBatchGuid(state);
      const trussThicknesses = new Set<number>();
      const existingAnalysisSetGuids = uniq(
        productionComponentInstancesByBatchGuid[batchBeingEditedGuid].map(
          (productionComponentInstance) => productionComponentInstance.analysisSetGuid,
        ),
      );
      const loadedTrussesByAnalysisSetGuid = getLoadedTrussesByAnalysisSetGuid(state);
      for (const existingAnalysisSetGuid of existingAnalysisSetGuids) {
        const existingTruss = loadedTrussesByAnalysisSetGuid[existingAnalysisSetGuid];
        trussThicknesses.add(existingTruss.thickness);
      }

      if (
        checkedAnalysisSetGuids.some((analysisSetGuidToAdd) => existingAnalysisSetGuids.includes(analysisSetGuidToAdd))
      ) {
        dispatch(enqueueError('Some of the components already exist in the batch.'));
        return;
      }

      const productionComponentInstancesNotBatchedByAnalysisSetGuid =
        getProductionComponentInstancesForActiveProductionGroupNotBatchedByAnalysisSetGuid(state);
      const productionComponentInstanceGuidsToUpdate = checkedAnalysisSetGuids
        .flatMap((analysisSetGuid) => productionComponentInstancesNotBatchedByAnalysisSetGuid[analysisSetGuid])
        .map((productionComponentInstance) => productionComponentInstance.guid);
      const updatedProductionComponentInstances = await designService.productionComponentInstances.patchAddToBatch(
        productionComponentInstanceGuidsToUpdate,
        batchBeingEditedGuid,
      );
      dispatch(productionComponentInstancesLoaded(updatedProductionComponentInstances));
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error adding to batch'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function deleteBatch(batch: Batch): AsyncThunkActionVoid {
  return async function deleteBatchThunkAction(dispatch, getState) {
    const spinnerId = dispatch(addSpinnerMessage('Deleting batch...'));

    try {
      // For a cleaner user experience, optimistically visibly delete the batch before making the multiple requests to
      // do so. If something goes wrong, the user will need to refresh, but in most cases this is preferable so that the
      // user does not need to observe the intermediate state of the components being removed from the batch before the
      // batch itself is deleted.
      const productionComponentInstanceGuids = getProductionComponentInstancesByBatchGuid(getState());
      dispatch(batchDeleted(batch.guid));

      const productionComponentInstanceGuidsForBatch = productionComponentInstanceGuids[batch.guid].map(
        (productionComponentInstance) => productionComponentInstance.guid,
      );
      await dispatch(removeProductionComponentInstancesFromBatch(productionComponentInstanceGuidsForBatch));

      await designService.batches.remove(batch.guid);
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error deleting batch.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function removeProductionComponentInstancesFromBatchByAnalysisSetGuid(
  analysisSetGuid: string,
  batchGuid: string,
): AsyncThunkActionVoid {
  return function removeProductionComponentInstancesFromBatchByAnalysisSetGuidThunkAction(dispatch, getState) {
    const productionComponentInstanceGuidsForAnalysisSetGuid = getProductionComponentInstancesByBatchGuid(getState())
      [batchGuid].filter(
        (productionComponentInstance) => productionComponentInstance.analysisSetGuid === analysisSetGuid,
      )
      .map((productionComponentInstance) => productionComponentInstance.guid);
    return dispatch(removeProductionComponentInstancesFromBatch(productionComponentInstanceGuidsForAnalysisSetGuid));
  };
}
